// Stats resolution: turn (current job level + job + history + equipment +
// buffs) into the ResolvedStats block the simulator consumes each tick.
//
// References:
// - docs/05-combat-sim §5.7.3 (Lv1 anchor table)
// - docs/05-combat-sim §5.7.5 (equipment caps: atk +400%, weapon x3, DPS x10)
// - docs/05-combat-sim §5.7.7 (default numbers: 1 stat = +1% atk, crit 5%/+150%)
// - docs/06-level-xp §6.10 (powerMult curve)
// - docs/06-level-xp §6.11 (HHMLL growth bands)
// - docs/06-level-xp §6.12 (permanent stat bonus from past jobs)
// - docs/06-level-xp §6.13 / ADR 0006 (per-Tier statContribution curve)
import { permanentStatBonusFromHistory } from '@renavora/content';
import { STAT_GROWTH_VALUE, STAT_KEYS, aggregateDungeonBoons, powerMult, statContribution, } from '@renavora/shared';
import { aggregateEquipment } from './equipment-affixes.js';
/** Numeric defaults from docs/05-combat-sim §5.7.7. */
export const STATS_DEFAULTS = {
    /** Per stat point contribution to atkBonus%. */
    atkBonusPerStat: 0.01,
    /** Base crit rate before gear. */
    baseCritRate: 0.05,
    /** Cap on crit rate (gear cannot push past this). */
    maxCritRate: 0.75,
    /** Base extra crit damage (above 1.0): 1.5 means crit hit is 2.5x. */
    baseCritDamage: 1.5,
    /** Cap on extra crit damage. */
    maxCritDamage: 5.0,
    /** Cap on aggregated atkBonus from gear+stats. 4.0 means +400%. */
    maxAtkBonus: 4.0,
    /** Per VIT point contribution to hpMax (added on top of base HP). */
    hpPerVit: 5,
    /** Per DEX point contribution to accuracy / evasion (decimal). */
    accuracyPerDex: 0.001,
    /** Lv1 hero HP anchor. */
    lv1HeroHp: 100,
    /** Lv1 weapon-base average. */
    lv1WeaponBase: 12.5,
    /** Lv1 attack interval (seconds). */
    lv1AttackInterval: 1.0,
    /** Default move speed (units/second). */
    baseMoveSpeed: 5,
};
/** Compute base stats from job + jobLevel + permanent history bonus. */
export function computeBaseStats(setup) {
    // Each stat starts at 10 baseline.
    const stats = { str: 10, dex: 10, int: 10, vit: 10, wis: 10 };
    // Current job growth: stat += growth_value * statContribution(jobLevel, tier).
    // Per ADR 0006 the contribution saturates per Tier (T1 saturates near Lv 500,
    // T4 keeps growing past Lv 990). Catalogue scaffolds without a growth band
    // contribute nothing.
    if (setup.job.growth) {
        const contribution = statContribution(setup.jobLevel, setup.job.tier);
        for (const key of STAT_KEYS) {
            const band = setup.job.growth[key];
            stats[key] += STAT_GROWTH_VALUE[band] * contribution;
        }
    }
    // Past-job permanent bonus uses the content-level reducer; ADR 0006 §2.2.3.
    if (setup.jobHistory !== undefined && setup.jobHistory.length > 0) {
        const defs = new Map();
        const history = {};
        for (const past of setup.jobHistory) {
            defs.set(past.job.id, past.job);
            history[past.job.id] = {
                maxLv: Math.max(history[past.job.id]?.maxLv ?? 0, past.maxLv),
            };
        }
        const bonus = permanentStatBonusFromHistory(history, defs);
        for (const key of STAT_KEYS) {
            stats[key] += bonus[key];
        }
    }
    return stats;
}
/** Resolve the runtime ResolvedStats block from a CharacterSetup. */
export function resolveStats(setup) {
    const stats = computeBaseStats(setup);
    const pow = powerMult(setup.jobLevel);
    const gear = aggregateEquipment(setup.equipment);
    const boons = aggregateDungeonBoons(setup.boons);
    // HP anchored at Lv1 then scaled by current job level. VIT adds a flat
    // bonus to keep stat-pumping relevant alongside level scaling. MP は廃止
    // (ADR 0003)。
    const hpMax = (STATS_DEFAULTS.lv1HeroHp * pow + stats.vit * STATS_DEFAULTS.hpPerVit + gear.hpFlat) *
        (1 + boons.hpMaxBonus);
    // Weapon base damage scales with current job level and gear's tier multiplier (1..3).
    const weaponBase = STATS_DEFAULTS.lv1WeaponBase * pow * gear.weaponBaseMult * (1 + boons.weaponBaseBonus);
    // atkBonus = stat × per-stat coefficient + gear aggregate. Capped per docs.
    const rawAtk = stats.str * STATS_DEFAULTS.atkBonusPerStat + gear.atkBonus;
    const atkBonus = Math.min(rawAtk, STATS_DEFAULTS.maxAtkBonus);
    // Crit numbers: base defaults + gear affixes, clamped under the docs caps.
    const critRate = Math.min(STATS_DEFAULTS.baseCritRate + gear.critRate + boons.critRateAdd, STATS_DEFAULTS.maxCritRate);
    const critDamage = Math.min(STATS_DEFAULTS.baseCritDamage + gear.critDamage + boons.critDamageAdd, STATS_DEFAULTS.maxCritDamage);
    // DEX feeds accuracy/evasion; gear affixes add on top. Attack speed shortens
    // the interval, move speed scales the base — both gear contributions are
    // fractions (e.g. +0.3 = +30%).
    const dexHitBonus = stats.dex * STATS_DEFAULTS.accuracyPerDex;
    return {
        hpMax,
        weaponBase,
        atkBonus,
        elementBonus: sumElementBonus(gear.elementBonus, boons.elementBonus),
        critRate,
        critDamage,
        armor: gear.armor * (1 + boons.armorBonus),
        resistance: gear.resistance,
        accuracy: dexHitBonus + gear.accuracy,
        evasion: dexHitBonus + gear.evasion + boons.evasionAdd,
        attackInterval: STATS_DEFAULTS.lv1AttackInterval / (1 + gear.attackSpeed),
        moveSpeed: STATS_DEFAULTS.baseMoveSpeed * (1 + gear.moveSpeed),
    };
}
function sumElementBonus(gear, boons) {
    const out = { ...gear };
    for (const [element, value] of Object.entries(boons)) {
        out[element] = (out[element] ?? 0) + (value ?? 0);
    }
    return out;
}
//# sourceMappingURL=stats-resolver.js.map